import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { map, mergeMap, Observable, of, switchMap, take } from 'rxjs';

import { EnvironmentService, ErrorHandlerService, UaService } from '@ecm/consumer';
import { EcmDispatcherModule, EnvUtils, StoreService } from '@ecm/dispatcher';
import { ContextService, Contract, ContractsBusinessModel, PortfolioHelper, PortfolioService } from '@ecm/portfolio';

import { Brand } from '../business/tms/brand.enum';
import { ContractData } from '../business/tms/contract-data';
import { TmsEvent } from '../business/tms/tms-event.enum';
import { UserStatus } from '../business/tms/user-status.enum';
import { TmsHelper } from '../utils/tms-helper';
import { TagStatus } from './tag-status.enum';

@Injectable({
    providedIn: 'root',
})
export class TMSService {
    private readonly className: string = 'TMSService';
    private static lastIdTracking = null;
    public static readonly LOG_MISSING_ID_SCOPE: string = 'TMS_MISSING_ID';
    public static readonly LOG_REQUEST_SCOPE: string = 'TMS_REQUEST';

    /**
     * Define the utag script tag state to avoid multiple includes
     */
    private utagScriptTagStatus: TagStatus;

    /**
     * Stack of callbacks. Represents every calls to track before the script is loaded.
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    private callbacks: Array<{ event: string; callback: Function }> = [];

    /**
     * Default utag data
     * Some values may be overridden
     */
    private utagData: any = {
        ns_site: 'orange-france-test',
        segment: 'RES',
        type_contrat: 'NR',
        type_ec: 'NR',
        couleur: 'Orange',
        couleur_contrat: 'NR',
        flag_acte: '0',
        titre_page: 'ECM',
        type_front: 'Client',
        type_page: 'hp',
        univers_affichage: 'global',
        canal: 'Web',
        domaine: 'Espace Client',
        id_visite: 'undefined',
        statut_loggue: 'non',
        compte_rattaches: 'NR',
        statut_compte_navigation: 'Titulaire',
        option: 'NR',
        offre_detenue: 'NR',
        type_acte: 'NR',
        type_equipement: 'NR',
        refonte: 'ECM',
        id_tracking: null, // id_tracking should be redefined to null to clean datalayer between 2 calls
    };

    public constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private environmentService: EnvironmentService,
        private portfolioService: PortfolioService,
        private contextService: ContextService,
        private uaService: UaService,
        private errorHandlerService: ErrorHandlerService,
    ) {
        this.utagScriptTagStatus = TagStatus.MISSING;
        if (!EnvUtils.isEclair()) {
            // TMS initialization
            this.init();
        }
    }

    /**
     * Tracks view in TMS
     * Merges current tracking data with current contract tracking data and provided tracking data, into a single object
     * Then pass this object to utag.track JavaScript API
     * Refer to "Specification for TMS ECM" doc file (utag.view tab)
     * @param {any} data Specific data layer values for the view
     * @param {boolean} [isMultiContracts=false] Set to `true` if view is multi-contracts (default value is `false`)
     */
    public trackView(data: any, isMultiContracts = false): void {
        this.setProperty('canal', this.uaService.isMobile() ? 'Mobile Web' : 'Web');

        // don't perform TMS tracking on ECLAIR mode
        if (EnvUtils.isEclair()) {
            return;
        }

        // ECQ-37375 : Displayed zone in page have to be tracked as link and not view (for id based issue)
        if (data?.titre_page?.startsWith('vue_')) {
            this.trackLink({
                track_nom: data.titre_page,
                track_zone: data.sous_univers,
                nonInteraction: true,
            });
            return;
        }

        // utag properties
        const STATUT_LOGGUE_KEY = 'statut_loggue';
        const OUI_KEY = 'oui';

        // default dataLayer, without contract tracking data
        const baseDataLayer: any = Object.assign({}, this.utagData, data);

        if (!baseDataLayer.id_tracking) {
            TMSService.lastIdTracking = null;
            this.errorHandlerService.logCustomError(
                new Error(`missing id_tracking ${JSON.stringify(data)}`),
                TMSService.LOG_MISSING_ID_SCOPE,
            );
        } else {
            TMSService.lastIdTracking = baseDataLayer.id_tracking;
            this.errorHandlerService.logCustomError(new Error(JSON.stringify(data)), TMSService.LOG_REQUEST_SCOPE);
        }

        // check if user is logged from `statut_loggue` property (set to `oui` by `AuthGuard` on logged routes)
        const isLogged: boolean =
            this.utagData[STATUT_LOGGUE_KEY] === OUI_KEY ||
            (data[STATUT_LOGGUE_KEY] && data[STATUT_LOGGUE_KEY] === OUI_KEY);

        if (isLogged) {
            // on logged routes, perform tracking with dataLayer enhanced by portfolio data
            this.getContractData(isMultiContracts)
                .pipe(
                    switchMap((contractData: ContractData) => {
                        return this.contextService.contextId$.pipe(
                            take(1),
                            map((cid: string) => ({ contractData, cid })),
                        );
                    }),
                    map(({ contractData, cid }: { contractData: ContractData; cid: string }) => {
                        const contractDataLayer: any = {
                            id_contrat: cid || 'NR',
                            compte_rattaches: contractData.linkedAccounts,
                            nombre_contrats: contractData.portfolioLength.toString(),
                            statut_compte_navigation: contractData.userStatus,
                            mixte_contrat: contractData.mixedContracts,
                            type_contrat: contractData.contractType,
                            type_ec: contractData.appTitle,
                            couleur_contrat: contractData.loggedContractBrand,
                            couleur: contractData.displayedBrand,
                            offre_detenue: contractData.offer,
                        };
                        if (contractData.departmentCode) {
                            contractDataLayer.departmentCode = contractData.departmentCode;
                        }
                        if (contractData.departmentLabel) {
                            contractDataLayer.departmentLabel = contractData.departmentLabel;
                        }
                        if (contractData.geographicalArea) {
                            contractDataLayer.geographicalArea = contractData.geographicalArea;
                        }
                        return Object.assign({}, this.utagData, contractDataLayer, data);
                    }),
                )
                .subscribe({
                    next: (dataLayer: any) => {
                        this.track(TmsEvent.VIEW, dataLayer);
                    },
                    error: () => {
                        EcmDispatcherModule.sendConsoleError(this.className, 'Error getting contract data');
                        // in case of error, perform tracking anyway with minimal dataLayer
                        this.track(TmsEvent.VIEW, baseDataLayer);
                    },
                });
        } else {
            // on visitor routes, perform tracking with minimal dataLayer
            this.track(TmsEvent.VIEW, baseDataLayer);
        }
    }

    /**
     * Tracks a new link click in TMS
     * Refer to "Specification for TMS ECM" doc file (utag.link tab)
     * @param {any} data Link related data
     */
    public trackLink(data: any): void {
        if (EnvUtils.isEclair()) {
            return;
        }
        data.track_nom = data.track_nom !== undefined ? TmsHelper.getFormattedLabel(data.track_nom) : '';
        data.track_cible = this.formatTrackCible(data.track_cible);
        data.id_tracking = TMSService.lastIdTracking;
        this.track(TmsEvent.LINK, data);
    }

    /**
     * Sets a property in Utag data
     * this method sets a local object, it doesn't update global TMS's utag_data object
     * @param {string} key Property key
     * @param {any} value Property value
     */
    public setProperty(key: string, value: any): void {
        if (!EnvUtils.isEclair()) {
            this.utagData[key] = value;
        }
    }

    /**
     * Call the newPage method of the lpTag object to tell LivePerson the view is loaded
     * @param {string} url
     * @param {{section?: any[], sdes?: any[], taglets?: any}} options
     */
    public notifyLoadedUrl(url: string, options: { section?: any[]; sdes?: any[]; taglets?: any } = {}): void {
        if (!EnvUtils.isEclair() && isPlatformBrowser(this.platformId)) {
            if (globalThis.lpTag === undefined || typeof globalThis.lpTag.newPage !== 'function') {
                return;
            }
            globalThis.lpTag.newPage(url, options);
        }
    }

    /**
     * Returns tracking data object related to current contract
     * based on /contracts API through `PortfolioService`
     * @param {boolean} isMultiContracts is tracked view a multi-contracts view ?
     * @returns {Observable<ContractData>}
     */
    private getContractData(isMultiContracts: boolean): Observable<ContractData> {
        return this.portfolioService.getRawContracts().pipe(
            mergeMap((contractsBusinessModel: ContractsBusinessModel) => {
                // `compte_rattaches` and `nombre_contrats` variables
                const contractData: ContractData = new ContractData();
                contractData.linkedAccounts = TmsHelper.getLinkedContracts(contractsBusinessModel.contracts);
                contractData.portfolioLength = contractsBusinessModel.contracts.length;

                // `statut_compte_navigation` variable
                const vimEligibleContract: Contract = PortfolioHelper.getVimEligibleContract(
                    contractsBusinessModel.contracts,
                );
                contractData.userStatus = vimEligibleContract !== undefined ? UserStatus.USER : UserStatus.OWNER;

                // `mixte_contrat` variable
                contractData.mixedContracts = isMultiContracts
                    ? TmsHelper.getMixedContracts(contractsBusinessModel.contracts)
                    : undefined;

                // get logged identity contract
                const loggedContract: Contract = PortfolioHelper.getLoggedContract(contractsBusinessModel.contracts);

                if (loggedContract) {
                    // logged identity variables (`type_contrat`, `type_ec`, `couleur_contrat`, `offre_detenue`)
                    contractData.contractType = loggedContract.telco.type;
                    if (loggedContract.brand) {
                        const isLoggedContractSosh: boolean = loggedContract.brand.toLowerCase() === 'sosh';
                        contractData.appTitle = 'Espace client ' + (isLoggedContractSosh ? Brand.SOSH : Brand.ORANGE);
                        contractData.loggedContractBrand = isLoggedContractSosh ? Brand.SOSH : Brand.ORANGE;
                    }
                    if (loggedContract.offerName) {
                        contractData.offer = loggedContract.offerName;
                    }
                    if (loggedContract.contractInstallationArea?.departmentCode) {
                        contractData.departmentCode = loggedContract.contractInstallationArea.departmentCode;
                    }
                    if (loggedContract.contractInstallationArea?.departmentLabel) {
                        contractData.departmentLabel = loggedContract.contractInstallationArea.departmentLabel;
                    }
                    if (loggedContract.contractInstallationArea?.geographicalArea) {
                        contractData.geographicalArea = loggedContract.contractInstallationArea.geographicalArea;
                    }

                    return this.contextService.isSosh().pipe(
                        map((isSosh: boolean) => {
                            // `couleur` variable
                            contractData.displayedBrand = isSosh ? Brand.SOSH : Brand.ORANGE;
                            return contractData;
                        }),
                    );
                } else {
                    return of(contractData);
                }
            }),
        );
    }

    private formatTrackCible(track_cible: any) {
        return track_cible !== undefined && track_cible !== 'NR' ? this.getFullUrl(track_cible) : 'NR';
    }

    /**
     * Insert <script> tag for utag loader
     * and executes a given callback
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    private insertScript(callback: Function): void {
        const env = this.environmentService.instant();

        if (env.API?.TRACKER?.TMS_URL) {
            // set script source based on TMS_URL environment
            const src: string = encodeURI(env.API.TRACKER.TMS_URL);
            // create script node
            const scriptElement: any = document.createElement('script');
            scriptElement.language = 'javascript';
            scriptElement.type = 'text/javascript';
            scriptElement.async = 1;
            scriptElement.charset = 'utf-8';
            scriptElement.src = src;
            if (env.FEATURES_TOGGLE && env.FEATURES_TOGGLE.TRACKER && env.FEATURES_TOGGLE.TRACKER.CUSTOM_EVENT) {
                document.addEventListener('utag.allTagsLoaded', () => callback(), false);
            } else {
                scriptElement.addEventListener('load', () => callback(), false);
            }

            // insert before first script node
            const firstScriptElement: any = document.getElementsByTagName('script')[0];
            firstScriptElement.parentNode.insertBefore(scriptElement, firstScriptElement);
        }
    }

    /**
     * TMS initialization
     */
    private init(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        const isTmsDisabled = this.environmentService.instant()?.API?.TRACKER?.TMS_DISABLED === true;
        if (isTmsDisabled) {
            EcmDispatcherModule.sendConsoleInfo(this.className, '🚀 TMS is explicitely disabled');
            globalThis.o_confCommon.tracking.Tealium.deactivate = true;
            globalThis.o_confCommon.tracking.Tealium.autoLoad.sync = false;
            globalThis.o_confCommon.tracking.Tealium.autoLoad.async = false;
            // Finally remove utag.js script if already present in DOM
            const utagScript = document.querySelector('script[src*="utag.sync.js"]');
            if (utagScript) {
                utagScript.parentNode.removeChild(utagScript);
            }
            return;
        }
        // init utag with specific property for SPA
        globalThis.utag_cfg_ovrd = { noview: true };
        globalThis.utag_data = globalThis.utag_data || {};
        // set session ID
        const sessionId: string = StoreService.has('session') ? StoreService.get('session')['id'] : 'undefined';
        this.setProperty('id_visite', sessionId);
    }

    /**
     * Generic tracking method that insert the utag script on first call
     * @param {TmsEvent} tealiumEvent (Possible values : `link` | `view`)
     * @param {any} data Full data layer
     */
    private track(tealiumEvent: TmsEvent, data: any): void {
        // Tracking only on Browser platform
        const env = this.environmentService.instant();
        const isTmsDisabled = env.API?.TRACKER?.TMS_DISABLED === true;
        if (isPlatformBrowser(this.platformId) && !isTmsDisabled) {
            const log: string =
                'Will perform ' + tealiumEvent + ' tracking with following data : ' + JSON.stringify(data);
            EcmDispatcherModule.sendConsoleInfo(this.className, log);

            if (globalThis.o_confCommon?.tracking?.Tealium?.deactivate === false) {
                this.trackThroughElcos(tealiumEvent, data);
            } else if (globalThis.utag === undefined && this.utagScriptTagStatus === TagStatus.MISSING) {
                this.addTrackingCallback(tealiumEvent, () => this.utagTrack(tealiumEvent, data));
                this.insertScript(() => this.unstackAllCallbacks());
                this.utagScriptTagStatus = TagStatus.ADDED;
            } else if (this.utagScriptTagStatus === TagStatus.ADDED) {
                this.addTrackingCallback(tealiumEvent, () => this.utagTrack(tealiumEvent, data));
            } else {
                this.utagTrack(tealiumEvent, data, () => this.trackElcosPerf(tealiumEvent));
            }
        }
    }

    private trackThroughElcos(tealiumEvent: TmsEvent, data: any) {
        try {
            if (tealiumEvent === TmsEvent.LINK) {
                if (this.environmentService.instant('FEATURES_TOGGLE.TRACKER.USE_UTAG_LINK', false) === true) {
                    this.utagTrack(tealiumEvent, data);
                } else {
                    globalThis.o_link(data);
                }
            } else {
                globalThis.o_utagview(data);
            }
        } catch (e) {
            EcmDispatcherModule.sendConsoleError(this.className, 'A catched error occured on TMS tracking via Elcos');
        }
    }

    /**
     * This facade is used to check that the utag.track function exists before to call it, to avoid errors
     * Some ad blockers seems to load the JS (onload on script fired) but don't interpret it (no utag)
     * A try/catch block has been added to avoid blocking click on links when an error occurs from utag.
     * @param {string} tealiumEvent
     * @param {any} data
     * @param {Function} [callback=null]
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    private utagTrack(tealiumEvent: string, data: any, callback: Function = null): void {
        if (globalThis.utag && globalThis.utag.track) {
            try {
                if (callback === null) {
                    globalThis.utag.track(tealiumEvent, data);
                } else {
                    globalThis.utag.track(tealiumEvent, data, callback);
                }
            } catch (error) {
                EcmDispatcherModule.sendConsoleError(this.className, 'A catched error occured on TMS tracking');
            }
        }
    }

    /**
     * Add a callback to the stack so it can be called on the onload of the utag script
     * @param {string} event
     * @param {Function} callback
     */
    // eslint-disable-next-line @typescript-eslint/ban-types
    private addTrackingCallback(event: string, callback: Function): void {
        this.callbacks.push({ event, callback });
    }

    /**
     * This method is called on the onload event of the utag script
     * It will call every stacked callbacks, starting with view before link
     */
    private unstackAllCallbacks(): void {
        this.utagScriptTagStatus = TagStatus.LOADED;
        this.callbacks.sort(({ event: a }, { event: b }) => (a === b ? 0 : a === 'view' ? -1 : 1));
        while (this.callbacks.length) {
            const { callback } = this.callbacks.shift();
            callback();
        }
    }

    /**
     * Performs Elcos performance tracking on view tracking
     * @param {TmsEvent} event
     */
    private trackElcosPerf(event: TmsEvent): void {
        if (event === TmsEvent.VIEW && globalThis.o_perf && globalThis.o_perf.init) {
            globalThis.o_perf.init();
        }
    }

    /**
     * Add host to the target if it's an internal route
     * @param {string} targetUrl Url or Uri
     * @returns {string} target or http://ecm_host/target
     */
    private getFullUrl(targetUrl: string): string {
        const pattern = /(http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/;
        if (isPlatformBrowser(this.platformId) && !pattern.test(targetUrl)) {
            return globalThis.location.origin.concat(targetUrl);
        }
        return targetUrl;
    }
}
